// Per-project linked repositories section — rendered inside ProjectSettingsPanel.
// Features:
//   - List of linked repos with full_name, provider badge, last_reconciled_at,
//     AI-automation toggle, and an unlink button (editor+ only).
//   - Add control: select picker of available repos (minus already linked) +
//     "Verknüpfen" button (editor+ only).
// The AI toggle PATCHes ai_automation_enabled. No reconcile trigger yet (P4).

import { useEffect, useState } from "preact/hooks";
import { GitBranch, Link2Off, Plus } from "lucide-preact";
import { toastSuccess, toastError } from "../lib/toast";
import { ApiError } from "../lib/api";
import {
  useRepoLinks,
  addRepoLink,
  removeRepoLink,
  toggleRepoLinkAi,
  toggleRepoLinkCreateFromGit,
  fetchAvailableRepos,
} from "../store/repoLinks";
import type { AvailableRepo } from "../lib/api";

interface Props {
  projectId: number;
  /** When false, all mutating controls are hidden (view-only role). */
  canEdit: boolean;
}

const LABEL_CLS =
  "block text-xs font-medium text-[color:var(--color-on-surface-variant)] mb-2";
// min-w-0 is load-bearing (#855): as a row-flex item a <select> keeps the
// intrinsic width of its widest <option> (min-width:auto), which pushed the
// "Verknuepfen" button out of the settings panel and made it scroll sideways.
const SELECT_CLS =
  "flex-1 min-w-0 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]";
const BTN_PRIMARY_CLS =
  "rounded bg-[color:var(--color-primary)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-primary)] hover:bg-[color:var(--color-primary-strong)] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";
const BTN_GHOST_CLS =
  "rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors disabled:opacity-50";

/** Simple relative date formatter — falls back to ISO slice. */
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    // Absichtlich stumm: rein darstellend. Wenn Intl das Datum nicht formatieren
    // kann, ist der ISO-Anfang immer noch lesbar — dafuer keinen Toast.
    return iso.slice(0, 10);
  }
}

/** Provider badge: small coloured pill with provider name. */
function ProviderBadge(props: { provider: string }): preact.JSX.Element {
  return (
    <span class="inline-flex items-center rounded-full border border-[color:var(--color-outline-variant)] px-1.5 py-0.5 text-label font-medium text-[color:var(--color-on-surface-variant)]">
      {props.provider}
    </span>
  );
}

/** Toggle switch — mirrors AiSettingsSection's enabled toggle. */
function ToggleSwitch(props: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
}): preact.JSX.Element {
  const { checked, onChange, ariaLabel, disabled } = props;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      class={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked
          ? "bg-[color:var(--color-primary)]"
          : "bg-[color:var(--color-surface-container-high)]"
      }`}
    >
      <span
        aria-hidden="true"
        class={`pointer-events-none inline-block h-4 w-4 rounded-full border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] transition-transform ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );
}

export function LinkedRepositoriesSection(props: Props): preact.JSX.Element {
  const { projectId, canEdit } = props;
  const { loading, items, error } = useRepoLinks(projectId);

  // Available repos for the add-picker
  const [available, setAvailable] = useState<AvailableRepo[]>([]);
  const [selectedRepoId, setSelectedRepoId] = useState<number | null>(null);
  const [linking, setLinking] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [unlinkingId, setUnlinkingId] = useState<number | null>(null);
  // Ohne diesen Zustand meldete das Auswahlfeld „Keine weiteren Repositories",
  // wenn der Abruf FEHLSCHLUG — nicht zu unterscheiden von „alle sind schon
  // verknuepft". api() toastet nur Mutationen, dieser GET also nicht.
  const [pickerError, setPickerError] = useState(false);

  // Fetch available repos whenever canEdit is true and we mount / projectId changes.
  useEffect(() => {
    if (!canEdit) return;
    setPickerError(false);
    fetchAvailableRepos(projectId).then(setAvailable).catch(() => {
      setPickerError(true);
      toastError("Verfügbare Repositories konnten nicht geladen werden.");
    });
  }, [projectId, canEdit]);

  // The linked repository_ids so we can filter them out of the picker
  const linkedRepoIds = new Set(items.map((l) => l.repository_id));
  const addableRepos = available.filter((r) => !linkedRepoIds.has(r.id));

  const handleLink = async (): Promise<void> => {
    if (!selectedRepoId) return;
    setLinking(true);
    try {
      await addRepoLink(projectId, selectedRepoId);
      setSelectedRepoId(null);
      toastSuccess("Repository verknüpft.");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toastError("Dieses Repository ist bereits verknüpft.");
      } else {
        toastError("Repository konnte nicht verknüpft werden.");
      }
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (linkId: number): Promise<void> => {
    setUnlinkingId(linkId);
    try {
      await removeRepoLink(projectId, linkId);
      toastSuccess("Repository entfernt.");
    } catch {
      toastError("Repository konnte nicht entfernt werden.");
    } finally {
      setUnlinkingId(null);
    }
  };

  const handleToggleAi = async (linkId: number, current: boolean): Promise<void> => {
    setTogglingId(linkId);
    try {
      await toggleRepoLinkAi(projectId, linkId, !current);
    } catch {
      toastError("KI-Automation konnte nicht geändert werden.");
    } finally {
      setTogglingId(null);
    }
  };

  const handleToggleCreate = async (linkId: number, current: boolean): Promise<void> => {
    setTogglingId(linkId);
    try {
      await toggleRepoLinkCreateFromGit(projectId, linkId, !current);
    } catch {
      toastError("Git→juuplan-Import konnte nicht geändert werden.");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <section>
      <div class="flex items-center gap-1.5 mb-3">
        <GitBranch size={14} class="text-[color:var(--color-on-surface-variant)]" aria-hidden="true" />
        <h3 class={LABEL_CLS + " mb-0"}>Verknüpfte Repositories</h3>
      </div>

      {loading ? (
        <p class="text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
      ) : error ? (
        <p class="text-xs text-[color:var(--color-error)]">Fehler: {error}</p>
      ) : items.length === 0 ? (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] mb-3">
          Keine Repositories verknüpft.
        </p>
      ) : (
        <ul class="flex flex-col gap-2 mb-3">
          {items.map((link) => (
            <li
              key={link.id}
              class="flex items-center gap-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-2"
            >
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-1.5 flex-wrap">
                  <span class="text-xs font-medium text-[color:var(--color-on-surface)] truncate">
                    {link.full_name}
                  </span>
                  <ProviderBadge provider={link.provider} />
                </div>
                {link.last_reconciled_at && (
                  <p class="text-label text-[color:var(--color-on-surface-variant)] mt-0.5">
                    Zuletzt synchronisiert: {formatDate(link.last_reconciled_at)}
                  </p>
                )}
              </div>

              {canEdit && (
                <div class="flex items-center gap-2 shrink-0">
                  {/* AI toggle (juuplan↔Git: erledigte Aufgaben schließen) */}
                  <div class="flex items-center gap-1.5" title="KI-Automation: automatischer Abgleich mit dem Repo (erledigte Aufgaben schließen)">
                    <ToggleSwitch
                      checked={link.ai_automation_enabled}
                      onChange={() => void handleToggleAi(link.id, link.ai_automation_enabled)}
                      ariaLabel={`KI-Automation für ${link.full_name}`}
                      disabled={togglingId === link.id}
                    />
                    <span class="text-label text-[color:var(--color-on-surface-variant)] hidden sm:inline">
                      KI
                    </span>
                  </div>

                  {/* Git→juuplan import toggle (fehlende, erledigte Arbeit anlegen) */}
                  <div class="flex items-center gap-1.5" title="Git→juuplan: in Git erledigte, in juuplan fehlende Arbeit automatisch als Aufgabe anlegen und zuweisen">
                    <ToggleSwitch
                      checked={link.create_from_git_enabled}
                      onChange={() => void handleToggleCreate(link.id, link.create_from_git_enabled)}
                      ariaLabel={`Git→juuplan-Import für ${link.full_name}`}
                      disabled={togglingId === link.id}
                    />
                    <span class="text-label text-[color:var(--color-on-surface-variant)] hidden sm:inline">
                      Git→juu
                    </span>
                  </div>

                  {/* Unlink button */}
                  <button
                    type="button"
                    onClick={() => void handleUnlink(link.id)}
                    disabled={unlinkingId === link.id}
                    aria-label={`Entfernen ${link.full_name}`}
                    title="Verknüpfung entfernen"
                    class={BTN_GHOST_CLS}
                  >
                    <Link2Off size={14} aria-hidden="true" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Add control — editor+ only */}
      {canEdit && (
        <div class="flex items-center gap-2">
          <select
            value={selectedRepoId ?? ""}
            onChange={(e) => {
              const val = (e.target as HTMLSelectElement).value;
              setSelectedRepoId(val ? parseInt(val, 10) : null);
            }}
            aria-label="Repo wählen"
            class={SELECT_CLS}
            disabled={linking || addableRepos.length === 0}
          >
            <option value="">
              {pickerError
                ? "Liste nicht geladen"
                : addableRepos.length === 0
                ? "Keine weiteren Repositories"
                : "Repository wählen…"}
            </option>
            {addableRepos.map((r) => (
              <option key={r.id} value={r.id}>
                {r.full_name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void handleLink()}
            disabled={!selectedRepoId || linking}
            aria-label="Verknüpfen"
            title="Repository verknüpfen"
            class={BTN_PRIMARY_CLS + " flex items-center gap-1"}
          >
            <Plus size={14} aria-hidden="true" />
            <span>Verknüpfen</span>
          </button>
        </div>
      )}

      {/* Informational note about the two automation directions */}
      {canEdit && (
        <p class="text-label text-[color:var(--color-on-surface-variant)] mt-2">
          <strong>KI</strong>: gleicht das Repo regelmäßig mit dem Projekt ab;
          Vorschläge zum Schließen erledigter Aufgaben (gemergte PRs / geschlossene
          Issues mit #Aufgaben-Referenz) bleiben offen und werden zur manuellen
          Prüfung angezeigt — du schließt selbst.{" "}
          <strong>Git→juu</strong>: legt für in Git bereits erledigte, in juuplan
          noch nicht erfasste Arbeit automatisch Aufgaben an (Status/Phase/Typ per
          KI, Zuständiger automatisch anhand der Git-E-Mail/-Kennung zugeordnet,
          immer als „KI" markiert).
        </p>
      )}
    </section>
  );
}
